import { actionCreators as fileUploadActions } from '@app/components/FileUpload/modules/fileUpload'
import { Area } from '@app/models/Area'
import { Assignment, IAssignment } from '@app/models/Assignment'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { AssociatedProperty } from '@app/models/AssociatedProperty'
import { Company } from '@app/models/Company'
import { Reminder } from '@app/models/Reminder'
import { ApplicationState } from '@app/store'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { EditorState } from 'draft-js'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface IAssignmentFormState {
  readonly assignment: IAssignment
  readonly isSaving: boolean
}

// initial state
const AssignmentFormStateRecord = Record({
  assignment: new Assignment(),
  isSaving: false,
})

export class AssignmentFormState extends AssignmentFormStateRecord
  implements IAssignmentFormState {
  public static fromJS(initialState?: Partial<IAssignmentFormState>) {
    if (!initialState) {
      return new AssignmentFormState()
    }

    const { assignment, ...rest } = initialState
    return new AssignmentFormState({
      assignment: Assignment.fromJS(assignment),
      ...rest,
    })
  }

  public readonly assignment: Assignment
  public readonly isSaving: boolean

  constructor(props?: Partial<IAssignmentFormState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.
export enum ActionTypes {
  InitializeAssignment = 'ASSIGNMENT_FORM::INITIALIZE_EDIT',
  LoadAssignment = 'ASSIGNMENT_FORM::LOAD_ASSIGNMENT',
  LoadAssignmentSuccess = 'ASSIGNMENT_FORM::LOAD_ASSIGNMENT_SUCCESS',
  ResetStore = 'ASSIGNMENT_FORM::RESET_STORE',
  SaveAssignmentForm = 'ASSIGNMENT_FORM::SAVE_ASSIGNMENT_FORM',
  SaveAssignmentFormFailure = 'ASSIGNMENT_FORM::SAVE_ASSIGNMENT_FORM_FAILURE',
  SaveAssignmentFormRequest = 'ASSIGNMENT_FORM::SAVE_ASSIGNMENT_FORM_REQUEST',
  SaveAssignmentFormSuccess = 'ASSIGNMENT_FORM::SAVE_ASSIGNMENT_FORM_SUCCESS',
  SetAssignedTo = 'ASSIGNMENT_FORM::SET_ASSIGNED_TO',
  SetCompany = 'ASSIGNMENT_FORM::SET_COMPANY',
  SetCompleted = 'ASSIGNMENT_FORM::SET_COMPLETED',
  SetContacts = 'ASSIGNMENT_FORM::SET_CONTACTS',
  SetDueDate = 'ASSIGNMENT_FORM::SET_DUE_DATE',
  SetLabel = 'ASSIGNMENT_FORM::SET_LABEL',
  SetLotSize = 'ASSIGNMENT_FORM::SET_LOT_SIZE',
  SetMarkup = 'ASSIGNMENT_FORM::SET_MARKUP',
  SetOpportunityDollar = 'ASSIGNMENT_FORM::SET_OPPORTUNITYDOLLAR',
  SetPriority = 'ASSIGNMENT_FORM::SET_PRIORITY',
  SetProperty = 'ASSIGNMENT_FORM::SET_PROPERTY',
  SetReminders = 'ASSIGNMENT_FORM::SET_REMINDERS',
  SetTitleText = 'ASSIGNMENT_FORM::SET_TITLE_TEXT',
}

interface IInitializeAssignmentAction {
  type: ActionTypes.InitializeAssignment
  assignment: Assignment
}
interface ILoadAssignmentAction {
  type: ActionTypes.LoadAssignment
  value?: string
}
interface ILoadAssignmentSuccessAction {
  type: ActionTypes.LoadAssignmentSuccess
  value: IAssignment
}
interface IResetStoreAction {
  type: ActionTypes.ResetStore
}
interface ISaveAssignmentFormAction {
  type: ActionTypes.SaveAssignmentForm
  value?: string
}
interface ISaveAssignmentFormFailureAction {
  type: ActionTypes.SaveAssignmentFormFailure
  error: string
}
interface ISaveAssignmentFormRequestAction {
  type: ActionTypes.SaveAssignmentFormRequest
}
interface ISaveAssignmentFormSuccessAction {
  type: ActionTypes.SaveAssignmentFormSuccess
}
interface ISetAssignedToAction {
  type: ActionTypes.SetAssignedTo
  assignedTo: List<AssociatedContact>
}
interface ISetCompanyAction {
  type: ActionTypes.SetCompany
  company?: Company
}
interface ISetCompletedAction {
  type: ActionTypes.SetCompleted
  completed: boolean
}
interface ISetContactsAction {
  type: ActionTypes.SetContacts
  contacts: List<AssociatedContact>
}
interface ISetDueDateAction {
  type: ActionTypes.SetDueDate
  dueDate?: number
}
interface ISetLabelAction {
  type: ActionTypes.SetLabel
  label: string
}
interface ISetLotSizeAction {
  type: ActionTypes.SetLotSize
  lotSize: Area
}
interface ISetMarkupAction {
  type: ActionTypes.SetMarkup
  markup: EditorState
}
interface ISetOpportunityDollarAction {
  type: ActionTypes.SetOpportunityDollar
  value: string
}
interface ISetPriorityAction {
  type: ActionTypes.SetPriority
  priority: string
}
interface ISetPropertyAction {
  type: ActionTypes.SetProperty
  properties: List<AssociatedProperty>
}
interface ISetRemindersAction {
  type: ActionTypes.SetReminders
  reminders: List<Reminder>
}
interface ISetTitleTextAction {
  type: ActionTypes.SetTitleText
  text: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IInitializeAssignmentAction
  | ILoadAssignmentAction
  | ILoadAssignmentSuccessAction
  | ISetTitleTextAction
  | ISetPriorityAction
  | ISetDueDateAction
  | ISetCompanyAction
  | ISetLabelAction
  | ISetContactsAction
  | ISetOpportunityDollarAction
  | ISetPropertyAction
  | ISetLotSizeAction
  | ISetCompletedAction
  | ISetMarkupAction
  | ISetAssignedToAction
  | ISetRemindersAction
  | ISaveAssignmentFormFailureAction
  | ISaveAssignmentFormRequestAction
  | ISaveAssignmentFormSuccessAction
  | IResetStoreAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
function initializeAssignment(
  assignment: Assignment,
): IInitializeAssignmentAction {
  return {
    assignment,
    type: ActionTypes.InitializeAssignment,
  }
}

function loadAssignment(value?: string): ILoadAssignmentAction {
  return { type: ActionTypes.LoadAssignment, value }
}

function loadAssignmentSuccess(
  value: IAssignment,
): ILoadAssignmentSuccessAction {
  return {
    type: ActionTypes.LoadAssignmentSuccess,
    value,
  }
}

function saveAssignmentForm(value?: string): ISaveAssignmentFormAction {
  return {
    type: ActionTypes.SaveAssignmentForm,
  }
}

function saveAssignmentFormFailure(
  error: string,
): ISaveAssignmentFormFailureAction {
  return {
    error,
    type: ActionTypes.SaveAssignmentFormFailure,
  }
}

function saveAssignmentFormRequest(): ISaveAssignmentFormRequestAction {
  return { type: ActionTypes.SaveAssignmentFormRequest }
}

function saveAssignmentFormSuccess(): ISaveAssignmentFormSuccessAction {
  return {
    type: ActionTypes.SaveAssignmentFormSuccess,
  }
}

function resetStore(): IResetStoreAction {
  return {
    type: ActionTypes.ResetStore,
  }
}

export const actionCreators = {
  initializeAssignment,
  loadAssignment,
  loadAssignmentSuccess,
  resetStore,
  saveAssignmentForm,
  saveAssignmentFormFailure,
  saveAssignmentFormSuccess,
  setAssignedTo: (
    assignedTo: List<AssociatedContact>,
  ): ISetAssignedToAction => ({ type: ActionTypes.SetAssignedTo, assignedTo }),
  setCompany: (company?: Company): ISetCompanyAction => ({
    company,
    type: ActionTypes.SetCompany,
  }),
  setCompleted: (completed: boolean): ISetCompletedAction => ({
    completed,
    type: ActionTypes.SetCompleted,
  }),
  setContacts: (contacts: List<AssociatedContact>): ISetContactsAction => ({
    contacts,
    type: ActionTypes.SetContacts,
  }),
  setDueDate: (dueDate?: number): ISetDueDateAction => ({
    dueDate,
    type: ActionTypes.SetDueDate,
  }),
  setLabel: (label: string): ISetLabelAction => ({
    label,
    type: ActionTypes.SetLabel,
  }),
  setLotSize: (lotSize: Area): ISetLotSizeAction => ({
    lotSize,
    type: ActionTypes.SetLotSize,
  }),
  setMarkup: (markup: EditorState): ISetMarkupAction => ({
    markup,
    type: ActionTypes.SetMarkup,
  }),
  setOpportunityDollar: (value: string): ISetOpportunityDollarAction => ({
    type: ActionTypes.SetOpportunityDollar,
    value,
  }),
  setPriority: (priority: string): ISetPriorityAction => ({
    priority,
    type: ActionTypes.SetPriority,
  }),
  setProperty: (properties: List<AssociatedProperty>): ISetPropertyAction => ({
    properties,
    type: ActionTypes.SetProperty,
  }),
  setReminders: (reminders: List<Reminder>): ISetRemindersAction => ({
    reminders,
    type: ActionTypes.SetReminders,
  }),
  setTitleText: (text: string): ISetTitleTextAction => ({
    text,
    type: ActionTypes.SetTitleText,
  }),
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<AssignmentFormState> = (
  state: AssignmentFormState = new AssignmentFormState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.InitializeAssignment:
      return state.set('assignment', action.assignment) as AssignmentFormState

    case ActionTypes.LoadAssignment:
      if (!action.value) {
        const newItem = new Assignment()
        return state.set('assignment', newItem) as AssignmentFormState
      } else {
        return state as AssignmentFormState
      }

    case ActionTypes.LoadAssignmentSuccess:
      return state.set('assignment', action.value) as AssignmentFormState

    case ActionTypes.ResetStore:
      return state.set('assignment', new Assignment()) as AssignmentFormState
    case ActionTypes.SaveAssignmentFormFailure:
      return state.set('isSaving', false) as AssignmentFormState
    case ActionTypes.SaveAssignmentFormRequest:
      return state.set('isSaving', true) as AssignmentFormState
    case ActionTypes.SaveAssignmentFormSuccess:
      return state.set('isSaving', false) as AssignmentFormState
    case ActionTypes.SetAssignedTo:
      return state.setIn(
        ['assignment', 'assignedTo'],
        action.assignedTo,
      ) as AssignmentFormState
    case ActionTypes.SetCompany:
      return state.setIn(
        ['assignment', 'company'],
        action.company,
      ) as AssignmentFormState
    case ActionTypes.SetCompleted:
      return state.setIn(
        ['assignment', 'isComplete'],
        action.completed,
      ) as AssignmentFormState
    case ActionTypes.SetContacts:
      return state.setIn(
        ['assignment', 'contacts'],
        action.contacts,
      ) as AssignmentFormState
    case ActionTypes.SetDueDate:
      return state.setIn(
        ['assignment', 'dueDate'],
        action.dueDate,
      ) as AssignmentFormState
    case ActionTypes.SetLabel:
      return state.setIn(
        ['assignment', 'label'],
        action.label,
      ) as AssignmentFormState
    case ActionTypes.SetLotSize:
      return state.setIn(
        ['assignment', 'lotSize'],
        action.lotSize,
      ) as AssignmentFormState
    case ActionTypes.SetMarkup:
      return state.setIn(
        ['assignment', 'markupState'],
        action.markup,
      ) as AssignmentFormState
    case ActionTypes.SetOpportunityDollar:
      return state.setIn(
        ['assignment', 'dollarOpportunity'],
        action.value,
      ) as AssignmentFormState
    case ActionTypes.SetPriority:
      return state.setIn(
        ['assignment', 'priority'],
        action.priority,
      ) as AssignmentFormState
    case ActionTypes.SetProperty:
      return state.setIn(
        ['assignment', 'properties'],
        action.properties,
      ) as AssignmentFormState
    case ActionTypes.SetReminders:
      return state.setIn(
        ['assignment', 'reminders'],
        action.reminders,
      ) as AssignmentFormState
    case ActionTypes.SetTitleText:
      return state.setIn(
        ['assignment', 'title'],
        action.text,
      ) as AssignmentFormState
    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadAssignmentSaga(action: ILoadAssignmentAction) {
  try {
    if (action.value) {
      const baseUrl = `/api/Assignments/${action.value}`

      const result = yield makeAuthenticatedRequest(baseUrl)

      if (result.success === true) {
        yield put(loadAssignmentSuccess(Assignment.fromJS(result.entity)))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

export function* saveAssignmentFormSaga(action: ISaveAssignmentFormAction) {
  try {
    yield put(saveAssignmentFormRequest())

    const state: AssignmentFormState = yield select(selectModuleState)

    let attachments = yield select(selectAttachments('assignment-form'))
    if (attachments) {
      attachments = attachments.valueSeq().map((item: any) => {
        if (!item.error) {
          return item.attachment
        }

        return false
      })
    } else {
      attachments = []
    }

    const assignment = state.assignment.toJS()
    assignment.attachments = attachments
    assignment.markup = convertToMarkup(
      state.assignment.markupState.getCurrentContent(),
    )

    const assignmentFormOpts = {
      body: JSON.stringify(assignment),
      method: assignment.id ? RequestMethod.PUT : RequestMethod.POST,
    }
    const url = assignment.id
      ? `/api/Assignments/${assignment.id}`
      : '/api/Assignments/Create'

    const result = yield makeAuthenticatedRequest(url, assignmentFormOpts)

    if (result.success === true) {
      yield put(saveAssignmentFormSuccess())
      yield put(
        successToast(
          'Success',
          assignment.id
            ? `${assignment.title} Updated`
            : 'New Assignment Created',
        ),
      )
      yield put(fileUploadActions.clearFileList('assignment-form'))

      if (assignment.id) {
        yield put(push(`/assignment/${assignment.id || result.entity.id}`))
      }
    } else {
      yield put(saveAssignmentFormFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveAssignmentFormFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* resetStoreSaga() {
  yield put(fileUploadActions.clearFileList('assignment-form'))
}

function* watchLoadAssignment() {
  yield takeLatest(ActionTypes.LoadAssignment, loadAssignmentSaga)
}

function* watchSaveAssignmentForm() {
  yield takeLatest(ActionTypes.SaveAssignmentForm, saveAssignmentFormSaga)
}

function* watchResetStore() {
  yield takeLatest(ActionTypes.ResetStore, resetStoreSaga)
}

export function* assignmentFormRootSaga() {
  yield fork(watchLoadAssignment)
  yield fork(watchSaveAssignmentForm)
  yield fork(watchResetStore)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = (state: ApplicationState): AssignmentFormState =>
  state.assignmentForm
const selectFileUploadState = (state: ApplicationState) => state.fileUpload

export const selectAssignment = () =>
  createSelector(selectModuleState, (mState) => mState.assignment)

export const selectAttachments = (key: string) =>
  createSelector(selectFileUploadState, (mState) => {
    return mState.files.get(key)
  })

export const selectIsSaving = () =>
  createSelector(selectModuleState, (mState) => mState.isSaving)
